<template>
  <Modal
    v-model:visible="isVisible"
    class="carrier-portal-event-modal"
    :header="
      event.id
        ? t(`enumMap.CarrierPortalAction.${event.eventType}`)
        : t('common.newParam', { param: t('events.events') })
    "
    size="md"
    @keypress.enter="onSave"
    @keypress.esc="onCancel"
  >
    <div class="flex flex-gap-1">
      <div class="w-6">
        <InputWrapper
          v-if="!event.id"
          v-slot="{ ariaProps, inputId }"
          input-id="event-type"
          class="field"
          :label="t('common.type')"
        >
          <RygenDropdown
            :id="inputId"
            v-model="event.eventType"
            v-bind="ariaProps"
            :options="eventTypeOptions"
            append-to="body"
            option-label="label"
            option-value="value"
            disabled
            @update:model-value="onTypeChange"
          />
        </InputWrapper>

        <InputWrapper
          v-slot="{ ariaProps, inputClasses, inputId, invalid }"
          input-id="event-severity"
          class="field"
          :label="t('events.severity')"
          :errors="v$.severity.$errors"
        >
          <RygenDropdown
            :id="inputId"
            v-model="v$.severity.$model"
            v-bind="ariaProps"
            :class="inputClasses"
            :invalid="invalid"
            :options="severityOptions"
            append-to="body"
            option-label="label"
            option-value="value"
            filter
            :placeholder="t('events.severity')"
          />
        </InputWrapper>

        <InputWrapper
          v-slot="{ ariaProps, inputClasses, inputId, invalid }"
          input-id="actual-date"
          class="field"
          :label="t('shipments.dates.actualDateTime')"
          :errors="v$.actualDate.$errors"
        >
          <div class="flex flex-gap-1 event-calendar-tz-wrapper">
            <RygenCalendar
              :id="inputId"
              v-model="event.actualDate"
              v-bind="ariaProps"
              :class="inputClasses"
              :invalid="invalid"
              :time-zone="event.actualLocation.timeZone"
              class="flex-grow-1"
              date-format="mm/dd/yy"
              show-icon
              show-time
              hour-format="12"
              :step-minute="15"
              :placeholder="t('shipments.dates.actualDateTime')"
            />
            <TimeZoneDropdown
              v-model="event.actualLocation.timeZone"
              :date-time-basis="event.actualDate"
              :disabled="!!event.relatedStopId"
              :placeholder="t('common.timezones')"
            />
          </div>
        </InputWrapper>

        <InputWrapper
          v-slot="{ ariaProps, inputId }"
          input-id="event-comment"
          class="field"
          :label="t('common.comments')"
          optional
        >
          <Textarea
            :id="inputId"
            v-model="event.comment"
            v-bind="ariaProps"
            rows="5"
            cols="30"
            :placeholder="t('common.comments')"
            @keypress.enter="$event.stopPropagation()"
            @keypress.shift.enter="onSave"
          />
        </InputWrapper>
      </div>

      <div class="w-6">
        <ShipmentEventModalStopSelector
          v-model="event"
          :shipment-stops="shipmentStops"
        />

        <!-- TODO: Use geolocation API to allow the user to autofill address -->
        <InputWrapper
          v-slot="{ ariaProps, inputId }"
          input-id="event-city"
          class="field"
          :label="t('address.city')"
          optional
        >
          <InputText
            :id="inputId"
            v-model="event.actualLocation.city"
            v-bind="ariaProps"
            :disabled="event.isMilestone || !!event.relatedStopId"
            :placeholder="t('address.city')"
          />
        </InputWrapper>

        <InputWrapper
          v-slot="{ ariaProps, inputId }"
          input-id="event-state"
          class="field"
          :label="t('address.state')"
          optional
        >
          <InputRegion
            :id="inputId"
            v-model="event.actualLocation.state"
            v-bind="ariaProps"
            :country="event.actualLocation.country"
            :disabled="event.isMilestone || !!event.relatedStopId"
            append-to="body"
          />
        </InputWrapper>

        <InputWrapper
          v-slot="{ ariaProps, inputId }"
          input-id="event-postal-code"
          class="field"
          :label="t('address.postalCode')"
          optional
        >
          <InputText
            :id="inputId"
            v-model="event.actualLocation.postalCode"
            v-bind="ariaProps"
            :disabled="event.isMilestone || !!event.relatedStopId"
            :placeholder="t('address.postalCode')"
          />
        </InputWrapper>
      </div>
    </div>

    <RelatedStopDates
      v-if="relatedStop && event.eventType !== ShipmentEventType.IN_TRANSIT"
      :related-stop="relatedStop"
    />

    <template #footer>
      <RygenButton
        mode="tertiary"
        :label="t('common.cancel')"
        @click="onCancel"
      />
      <RygenButton
        mode="primary"
        icon="pi pi-save"
        :disabled="disabled"
        :label="t('common.saveParam', { param: t('events.events') })"
        @click="onSave"
      />
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import { computed, ComputedRef, PropType, ref, Ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { required, useUid, useValidations } from '@rygen/rygen-ui/composables';
import { DropdownOption } from '@rygen/rygen-ui/model';

import {
  isShipmentDeliveryEvent,
  isShipmentOriginEvent,
  ShipmentEvent,
  ShipmentEventLocation,
  ShipmentEventType,
  ShipmentStop,
} from '@/model';
import { buildShipmentEvent } from '@/util';

import RelatedStopDates from '@/components/carrierPortal/event/RelatedStopDates.vue';
import ShipmentEventModalStopSelector from '@/components/shipment/ShipmentEventModalStopSelector.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import InputRegion from '@rygen/rygen-ui/components/form/InputRegion.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenCalendar from '@rygen/rygen-ui/components/form/RygenCalendar.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';
import TimeZoneDropdown from '@rygen/rygen-ui/components/form/TimeZoneDropdown.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';

const isVisible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  shipmentEvent: {
    type: Object as PropType<ShipmentEvent>,
    default: () => new ShipmentEvent(),
  },
  eventTypeOptions: {
    type: Array as PropType<DropdownOption<string>[]>,
    default: () => [],
  },
  severityOptions: {
    type: Array as PropType<DropdownOption<string>[]>,
    default: () => [],
  },
  shipmentStops: {
    type: Array as PropType<ShipmentStop[]>,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
});
const {
  shipmentEvent,
  eventTypeOptions,
  severityOptions,
  shipmentStops,
  disabled,
} = toRefs(props);
const emit = defineEmits<{
  (event: 'cancel'): void;
  (event: 'save', shipmentEvent: ShipmentEvent): void;
}>();

const { t } = useI18n();
const { uid } = useUid('carrier-portal-event-modal');

// Carrier Portal doesn't allow adding milestones, so the rules below ignore
// milestone requirements
const event: Ref<ShipmentEvent> = ref(buildShipmentEvent(shipmentEvent.value));
const rules = computed(() => ({
  severity: {
    required: required(t('events.severity')),
  },
  actualDate: {
    required: required(t('shipments.dates.actualDateTime')),
  },
}));
const { v$, validate } = useValidations(event, rules, uid);

const shipmentStopOptions: ComputedRef<ShipmentStop[]> = computed(() => {
  if (!shipmentStops.value.length) {
    return [];
  }

  if (event.value.relatedStopId) {
    return shipmentStops.value.filter(
      (s) => s.id === event.value.relatedStopId,
    );
  }

  if (event.value.eventType) {
    if (isShipmentOriginEvent(event.value.eventType)) {
      return shipmentStops.value.filter((s) => s.isPickup);
    } else if (isShipmentDeliveryEvent(event.value.eventType)) {
      return shipmentStops.value.filter((s) => s.isDropOff);
    }
  }
  return shipmentStops.value;
});

const shipmentStopById = computed(() => {
  return shipmentStops.value.reduce(
    (acc, stop) => {
      if (stop.id) {
        acc[stop.id] = stop;
      }
      return acc;
    },
    {} as Record<number, ShipmentStop>,
  );
});

const relatedStop = computed(() => {
  return event.value.relatedStopId
    ? shipmentStopById.value[event.value.relatedStopId]
    : undefined;
});

const onSave = async () => {
  if (await validate()) {
    emit('save', event.value);
    isVisible.value = false;
  }
};

const onCancel = () => {
  emit('cancel');
  isVisible.value = false;
};

const reset = () => {
  v$.value.$reset();
};

const onTypeChange = (newEventType: ShipmentEventType) => {
  if (isShipmentOriginEvent(newEventType)) {
    event.value.relatedStopId = shipmentStops.value.find(
      (stop) => stop.isPickup,
    )?.id;
  } else if (isShipmentDeliveryEvent(newEventType)) {
    event.value.relatedStopId = shipmentStops.value.find(
      (stop) => stop.isDropOff,
    )?.id;
  }
};

watch(
  isVisible,
  (newVal: boolean) => {
    if (!newVal) {
      event.value = new ShipmentEvent();
      return;
    }

    event.value = buildShipmentEvent(shipmentEvent.value);
    reset();
  },
  { immediate: true },
);

watch(shipmentStopOptions, (stopOptions: ShipmentStop[]) => {
  if (stopOptions.length === 1) {
    event.value.relatedStopId = stopOptions[0].id;
  }
});

watch(
  () => ({ id: event.value.id, relatedStopId: event.value.relatedStopId }),
  (newEvent?: any, oldEvent?: any) => {
    const relatedStopIdUpdated =
      newEvent?.id === oldEvent?.id &&
      newEvent?.relatedStopId !== oldEvent?.relatedStopId;

    if (relatedStopIdUpdated) {
      const newRelatedStopId = newEvent?.relatedStopId;
      const oldRelatedStopId = oldEvent?.relatedStopId;

      const newRelatedStop = shipmentStops.value.find(
        (stop) => stop.id === newRelatedStopId,
      );
      const oldRelatedStop = shipmentStops.value.find(
        (stop) => stop.id === oldRelatedStopId,
      );

      const actualLocation =
        event.value.actualLocation ?? new ShipmentEventLocation();
      const oldActualLocationIsEmpty =
        !actualLocation.city &&
        !actualLocation.state &&
        !actualLocation.postalCode;
      const oldActualLocationIsOldStop =
        !oldActualLocationIsEmpty &&
        ((oldRelatedStop?.location?.city === actualLocation.city &&
          oldRelatedStop?.location?.state === actualLocation.state) ||
          oldRelatedStop?.location?.postalCode === actualLocation.postalCode);
      const shouldUpdateLocation =
        oldActualLocationIsEmpty || oldActualLocationIsOldStop;
      if (shouldUpdateLocation) {
        event.value.actualLocation = new ShipmentEventLocation({
          ...newRelatedStop?.location,
        });
      }
    }
  },
  { deep: true },
);
</script>
